// src/pages/MFAWaiting/MFAWaitingPage.tsx
import { FC, useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { mfaService } from 'features/mfa';
import Button, { Button_Style, Button_Type } from 'shared/ui/Button/Button';
import './MFAWaitingPage.scss';
import { delete_files } from "features/ai-assistant";
import { UserContext } from "features/account";
import { MFASetupModal } from "features/mfa";
import notificationAnimation from "assets/lottie/Notification.json";
import Lottie from "lottie-react";


const MFAWaitingPage: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<'waiting' | 'approved' | 'timeout'>('waiting');
  const [timeLeft, setTimeLeft] = useState(120);
  const [mfaToken, setMfaToken] = useState('');
  const [error, setError] = useState('');
  const { user, setAi_conversation } = useContext(UserContext);
  const [mfaEmail, setMfaEmail] = useState<string>('');
  const [showMFASetupModal, setShowMFASetupModal] = useState(false);

  useEffect(() => {
    const state = location.state as any;
    if (state?.mfaToken) {
      setMfaToken(state.mfaToken);
      setMfaEmail(state.mfaEmail);
      handleResendNotification(state?.mfaToken);
    } else {
      navigate(-1);
    }
  }, [location, navigate]);

  useEffect(() => {
    let pollInterval: NodeJS.Timeout;

    if (status === 'waiting' && mfaToken) {
      pollInterval = setInterval(async () => {
        try {
          const data = await mfaService.polling(mfaToken);

          if (data.status === 'approved') {
            setStatus('approved');
            clearInterval(pollInterval);
            localStorage.setItem('type', data.userData?.type);

            if ((data.userData?.type === "individual")) {
              await delete_files(user?.clientNumber!);
              localStorage.removeItem("assistantConversation");
              setAi_conversation([]);
              if ((data.userData?.mustRedirectToIDAssistant)) {
                setTimeout(() => navigate("/particuliers/id-assistant/add/standard"), 1500);
              } else {
                setTimeout(() => navigate("/particuliers/dashboard"), 1500);
              }
            } else if ((data.userData?.type === "partner")) {
              if (!!+(process.env.REACT_APP_FOR_PARTNER_ONLY || 0)) {
                setTimeout(() => navigate("/professionnels/base-de-donnees"), 1500);
              } else {
                setTimeout(() => navigate("/professionnels/dashboard"), 1500);
              }
            }
          } else if (data.status === 'expired') {
            setStatus('timeout');
            clearInterval(pollInterval);
          }
        } catch (err) {
          console.error('Polling error:', err);
        }
      }, 3000);
    }

    return () => clearInterval(pollInterval);
  }, [status, mfaToken, navigate, user, setAi_conversation]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (status === 'waiting' && timeLeft > 0) {
      timer = setTimeout(() => setTimeLeft(prev => prev - 1), 1000);
    } else if (timeLeft === 0 && status === 'waiting') {
      setStatus('timeout');
    }
    return () => clearTimeout(timer);
  }, [status, timeLeft]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleResendNotification = async (myMfaToken?: string) => {
    try {
      if (!myMfaToken) {
        setTimeLeft(120);
        setStatus("waiting");
      }
      await mfaService.resendPushNotification(myMfaToken ?? mfaToken);
      setError('');
    } catch (err) {
      setError('Erreur lors du renvoi');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleTryAnotherMethod = () => {
    setShowMFASetupModal(true);
    setTimeLeft(120);
    setStatus("waiting");
  };

  const handleMFASetupClose = () => {
    setShowMFASetupModal(false);
  };

  const handleMFASetupSuccess = () => {
    setShowMFASetupModal(false);
  };

  if (status === 'approved') {
    return (
      <div className="mfa-waiting-page">
        <div className="container">
          <div className="card success">
            <div className="icon">✓</div>
            <h2>Connexion approuvée</h2>
            <p>Redirection en cours...</p>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'timeout' && !showMFASetupModal) {
    return (
      <div className="mfa-waiting-page">
        <div className="container">
          <div className="card timeout">
            <div className="icon">!</div>
            <h2>Délai expiré</h2>
            <p>La demande de connexion a expiré.</p>
            <div className="actions">
              <Button
                type={Button_Type.PRIMARY}
                style={Button_Style.SOLID}
                label="Renvoyer"
                onClick={() => handleResendNotification()}
              />
              <Button
                type={Button_Type.SECONDARY}
                style={Button_Style.OUTLINED}
                label="Autre méthode"
                onClick={handleTryAnotherMethod}
              />
              <Button
                type={Button_Type.SECONDARY}
                style={Button_Style.OUTLINED}
                label="Retour"
                onClick={() => navigate('/')}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mfa-waiting-page">
        <div className="container">
          <div className="card waiting">
            <div className="loader">
              <div className="loader-ring"></div>
              <div className="loader-icon">
                <Lottie animationData={notificationAnimation} loop style={{ width: 48, height: 48 }} />
              </div>
            </div>

            <h2>Vérification mobile</h2>
            <p className="description">
              Une notification a été envoyée à votre mobile.<br />
              Veuillez confirmer la connexion sur votre téléphone.
            </p>

            <div className="timer">
              <span>Expiration</span>
              <strong>{formatTime(timeLeft)}</strong>
            </div>

            <div className="actions">
              <Button
                type={Button_Type.SECONDARY}
                style={Button_Style.OUTLINED}
                label="Renvoyer"
                onClick={() => handleResendNotification()}
              />
              <Button
                type={Button_Type.SECONDARY}
                style={Button_Style.OUTLINED}
                label="Autre méthode"
                onClick={handleTryAnotherMethod}
              />
            </div>

            {error && <div className="error">{error}</div>}

            <div className="help">
              <p>Vous n'avez pas reçu la notification ?</p>
              <ul>
                <li>Vérifiez que l'application est installée</li>
                <li onClick={() => {
                  window.open("https://idprotect.fr/contact/", "_blank", "noopener,noreferrer");
                }}>Contactez le support client</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      {/* Modal pour choisir une autre méthode MFA */}
      {showMFASetupModal && (
        <MFASetupModal
          isOpen={showMFASetupModal}
          onClose={handleMFASetupClose}
          onSuccess={handleMFASetupSuccess}
          userEmail={mfaEmail}
          fromLogin={true}
          mfaToken={mfaToken}
        />
      )}
    </>
  );
};

export default MFAWaitingPage;